// story_scene — a full-screen illustrated story page: illustration + narration +
// tap-to-reveal vocabulary chips. Presentational. The vocab chips reveal their
// gloss (and play audio) on tap so a kid can self-serve unfamiliar words.

import { useEffect, useState, type ReactElement } from 'react';

import type { content } from '@fajrarabi/zod-schemas';

import { ActivityImage, ArabicText, AudioButton } from './primitives';
import type { ActivityRendererProps } from './types';

export function StoryScene({
  data,
  ctx,
  cb,
}: ActivityRendererProps<content.StorySceneData>): ReactElement {
  const { t } = ctx;
  useEffect(() => {
    cb.onViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [revealed, setRevealed] = useState<string | null>(null);

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col">
      <div className="overflow-hidden rounded-3xl shadow-[0_24px_60px_-30px_rgba(31,26,23,0.35)] ring-1 ring-cream-200">
        <div className="w-full">
          <ActivityImage src={data.image} ctx={ctx} alt={t(data.bodyKey)} fallbackEmoji="📖" />
        </div>
      </div>

      <div className="mt-5 rounded-3xl bg-white p-5 text-xl leading-relaxed text-ink-900 ring-1 ring-cream-200">
        <ArabicText text={t(data.bodyKey)} ctx={ctx} />
        {data.audio ? (
          <div className="mt-4">
            <AudioButton src={data.audio} ctx={ctx} label={t('content.common.play_audio')} />
          </div>
        ) : null}
      </div>

      {data.vocab && data.vocab.length > 0 ? (
        <div className="mt-5">
          <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-500">
            {t('content.story_scene.new_words')}
          </p>
          <div className="flex flex-wrap gap-2">
            {data.vocab.map((v) => {
              const open = revealed === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setRevealed((cur) => (cur === v.id ? null : v.id))}
                  aria-expanded={open}
                  className={`inline-flex min-h-[44px] items-center gap-2 rounded-full border-2 px-4 text-lg font-bold transition-colors ${
                    open
                      ? 'border-sun-500 bg-sun-50 text-burgundy-700'
                      : 'border-cream-200 bg-white text-ink-900 hover:border-sun-300'
                  }`}
                >
                  <ArabicText text={t(v.wordKey)} ctx={ctx} />
                  {open && v.glossKey ? (
                    <span className="text-sm font-medium text-ink-500">— {t(v.glossKey)}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
